import { useEffect, useMemo, useRef, useState, type DragEvent, JSX } from "react";
import useBuilderStore from "src/store/builderStore";

export type BuilderCardTemplate = {
  id: string;
  label: string;
  category: string;
  description: string;
  grid: {
    w: number;
    h: number;
    minW: number;
    minH: number;
    maxW: number;
    maxH: number;
  };
};

export const BUILDER_CARD_TEMPLATES: BuilderCardTemplate[] = [
  {
    id: "compact",
    label: "Compact KPI",
    category: "Small",
    description:
      "Best for scorecards, badges, or a single KPI with a short trend.",
    grid: {
      w: 4,
      h: 4,
      minW: 4,
      minH: 4,
      maxW: 8,
      maxH: 6,
    },
  },
  {
    id: "standard",
    label: "Standard Insight",
    category: "Balanced",
    description:
      "A reliable chart size for bar, pie, and short line comparisons.",
    grid: {
      w: 8,
      h: 6,
      minW: 6,
      minH: 6,
      maxW: 12,
      maxH: 8,
    },
  },
  {
    id: "wide",
    label: "Wide Trend",
    category: "Landscape",
    description:
      "Works well for time series, dense legends, and tables with more columns.",
    grid: {
      w: 12,
      h: 6,
      minW: 8,
      minH: 6,
      maxW: 16,
      maxH: 8,
    },
  },
  {
    id: "tall",
    label: "Tall Story",
    category: "Portrait",
    description:
      "Useful for funnels, ranked lists, or drilldowns with a long vertical scan.",
    grid: {
      w: 8,
      h: 10,
      minW: 6,
      minH: 8,
      maxW: 12,
      maxH: 12,
    },
  },
  {
    id: "hero",
    label: "Hero Panel",
    category: "Large",
    description:
      "A front-row placement for executive summaries and composite analytics blocks.",
    grid: {
      w: 16,
      h: 10,
      minW: 12,
      minH: 8,
      maxW: 24,
      maxH: 12,
    },
  },
];

export type BuilderWidgetTemplate = {
  id: string;
  templateId: number;
  code: string;
  title: string;
  chartType: string;
  defaultGridW: number;
  defaultGridH: number;
  minGridW: number;
  minGridH: number;
  maxGridW: number;
  maxGridH: number;
  category: string;
  description: string;
  icon: string;
  group: string;
};

export const BUILDER_WIDGET_DRAG_TYPE = "application/x-builder-widget";

export const BUILDER_WIDGET_TEMPLATES: BuilderWidgetTemplate[] = [
  {
    id: "kpi",
    templateId: 0,
    code: "kpi",
    title: "KPI",
    chartType: "kpi",
    defaultGridW: 4,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Metric",
    description:
      "Surface a single value with a focused summary for high-signal monitoring.",
    icon: "bi-123",
    group: "Metrics",
  },
  {
    id: "kpi_with_detail",
    templateId: 0,
    code: "kpi_with_detail",
    title: "KPI With Detail",
    chartType: "kpi_with_detail",
    defaultGridW: 4,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Metric",
    description:
      "Pair a KPI headline with deeper supporting context and a drill-through affordance.",
    icon: "bi-layout-text-window-reverse",
    group: "Metrics",
  },
  {
    id: "multi_line",
    templateId: 0,
    code: "multi_line",
    title: "Multi Line Chart",
    chartType: "multi_line",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Trend",
    description:
      "Compare multiple series over time when direction and crossover matter.",
    icon: "bi-graph-up-arrow",
    group: "Trends",
  },
  {
    id: "pie",
    templateId: 0,
    code: "pie",
    title: "Pie Chart",
    chartType: "pie",
    defaultGridW: 4,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Distribution",
    description:
      "Show part-to-whole contribution for a compact set of categories.",
    icon: "bi-pie-chart",
    group: "Distributions",
  },
  {
    id: "pie_drilldown",
    templateId: 0,
    code: "pie_drilldown",
    title: "Pie Drilldown",
    chartType: "pie_drilldown",
    defaultGridW: 4,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Distribution",
    description:
      "Start from a high-level split, then inspect category breakdowns in detail.",
    icon: "bi-diagram-3",
    group: "Distributions",
  },
  {
    id: "data_table",
    templateId: 0,
    code: "data_table",
    title: "Data Table",
    chartType: "data_table",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Tabular",
    description:
      "Expose detailed records when users need exact values, sorting, and scanability.",
    icon: "bi-table",
    group: "Tabular",
  },
  {
    id: "map",
    templateId: 0,
    code: "map",
    title: "Map",
    chartType: "map",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Geography",
    description:
      "Plot regional performance when spatial patterns matter more than rank order.",
    icon: "bi-geo-alt",
    group: "Geography",
  },
  {
    id: "force_bar",
    templateId: 0,
    code: "force_bar",
    title: "Force Bar",
    chartType: "force_bar",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Process",
    description:
      "Show stacked stage durations in a single horizontal force-style bar.",
    icon: "bi-bar-chart-steps",
    group: "Process",
  },
  {
    id: "stacked_bar",
    templateId: 0,
    code: "stacked_bar",
    title: "Stacked Bar",
    chartType: "stacked_bar",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Process",
    description:
      "Compare completed-with-SLA versus outside-SLA counts in a single stacked bar.",
    icon: "bi-bar-chart-line",
    group: "Process",
  },
  {
    id: "time_series_bar",
    templateId: 0,
    code: "time_series_bar",
    title: "Time Series Bar",
    chartType: "time_series_bar",
    defaultGridW: 6,
    defaultGridH: 3,
    minGridW: 2,
    minGridH: 2,
    maxGridW: 12,
    maxGridH: 12,
    category: "Process",
    description:
      "Track completed with and without SLA over time using stacked columns.",
    icon: "bi-bar-chart",
    group: "Process",
  },
];

type SidebarProps = {
  onAddWidget?: (widget: BuilderWidgetTemplate) => void;
};

const formatChartTypeLabel = (value: string): string =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

export default function Sidebar({ onAddWidget }: SidebarProps): JSX.Element {
  const [openWidgetGroup, setOpenWidgetGroup] = useState<string | null>(null);
  const [groupSearchQueries, setGroupSearchQueries] = useState<
    Record<string, string>
  >({});
  const [highlightedWidgetId, setHighlightedWidgetId] = useState<string | null>(
    null,
  );
  const highlightedWidgetTimeoutRef = useRef<number | null>(null);
  const dashboardTemplates = useBuilderStore(
    (state) => state.dashboardTemplates,
  );
  const widgetGroups = useMemo(() => {
    const groups = new Map<string, BuilderWidgetTemplate[]>();

    dashboardTemplates.forEach((widget) => {
      const existingGroup = groups.get(widget.group) ?? [];
      existingGroup.push(widget);
      groups.set(widget.group, existingGroup);
    });

    return Array.from(groups.entries()).sort(([left], [right]) =>
      left.localeCompare(right),
    );
  }, [dashboardTemplates]);

  useEffect(() => {
    if (widgetGroups.length === 0) {
      setOpenWidgetGroup(null);
      return;
    }

    if (
      openWidgetGroup &&
      !widgetGroups.some(([groupName]) => groupName === openWidgetGroup)
    ) {
      setOpenWidgetGroup(null);
    }
  }, [openWidgetGroup, widgetGroups]);

  const toggleWidgetGroup = (chartType: string) => {
    setOpenWidgetGroup((currentGroup) => {
      const closing = currentGroup === chartType;
      if (closing) {
        setGroupSearchQueries((prev) => {
          const next = { ...prev };
          delete next[chartType];
          return next;
        });
      }
      return closing ? null : chartType;
    });
  };

  const handleGroupSearch = (group: string, query: string) => {
    setGroupSearchQueries((prev) => ({ ...prev, [group]: query }));
  };

  const [draggingWidgetId, setDraggingWidgetId] = useState<string | null>(null);

  const handleWidgetClick = (widget: BuilderWidgetTemplate) => {
    onAddWidget?.(widget);
    setHighlightedWidgetId(widget.id);

    if (highlightedWidgetTimeoutRef.current !== null) {
      window.clearTimeout(highlightedWidgetTimeoutRef.current);
    }

    highlightedWidgetTimeoutRef.current = window.setTimeout(() => {
      setHighlightedWidgetId((currentWidgetId) =>
        currentWidgetId === widget.id ? null : currentWidgetId,
      );
      highlightedWidgetTimeoutRef.current = null;
    }, 2200);
  };

  const handleDragStart = (
    event: DragEvent<HTMLButtonElement>,
    widget: BuilderWidgetTemplate,
  ) => {
    event.dataTransfer.setData(
      BUILDER_WIDGET_DRAG_TYPE,
      JSON.stringify(widget),
    );
    event.dataTransfer.effectAllowed = "copy";
    setDraggingWidgetId(widget.id);
  };

  const handleDragEnd = () => {
    setDraggingWidgetId(null);
  };

  useEffect(() => {
    return () => {
      if (highlightedWidgetTimeoutRef.current !== null) {
        window.clearTimeout(highlightedWidgetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <aside className="builder-sidebar">
      <div className="builder-sidebar__header">
        <span className="builder-eyebrow">Builder Library</span>
        <h2 className="builder-sidebar__title mb-2">Widgets</h2>
        <p className="builder-sidebar__copy mb-0">
          Click or drag any widget to add it to the builder canvas.
        </p>
      </div>
      <div className="builder-sidebar__tabs">
        <div className="builder-sidebar__panel">
          <p className="builder-sidebar__panel-copy mb-0">
            Review widget types and click to add them to the canvas.
          </p>

          <div className="builder-sidebar__widgets">
            {widgetGroups.map(([chartType, widgets]) => {
              const searchQuery = groupSearchQueries[chartType] ?? "";
              const filteredWidgets = searchQuery
                ? widgets.filter((w) =>
                    w.title.toLowerCase().includes(searchQuery.toLowerCase()),
                  )
                : widgets;

              return (
                <section key={chartType} className="d-flex flex-column gap-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-between"
                    onClick={() => toggleWidgetGroup(chartType)}
                    aria-expanded={openWidgetGroup === chartType}
                  >
                    <span className="small text-uppercase text-muted fw-semibold">
                      {formatChartTypeLabel(chartType)}
                    </span>
                    <span className="small text-muted d-flex align-items-center gap-2">
                      <span>
                        {searchQuery && openWidgetGroup === chartType
                          ? `${filteredWidgets.length} / ${widgets.length}`
                          : widgets.length}
                      </span>
                      <i
                        className={`bi ${
                          openWidgetGroup === chartType
                            ? "bi-chevron-up"
                            : "bi-chevron-down"
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                  </button>

                  {openWidgetGroup === chartType && (
                    <div className="builder-widget-group__list">
                      <div
                        className="builder-widget-group__search"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          background: "var(--bs-body-bg)",
                          paddingBottom: "6px",
                        }}
                      >
                        <div className="input-group input-group-sm">
                          <span
                            className="input-group-text bg-transparent border-end-0"
                            style={{ borderRadius: "6px 0 0 6px" }}
                          >
                            <i
                              className="bi bi-search text-muted"
                              style={{ fontSize: "0.7rem" }}
                            />
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0 ps-0 shadow-none"
                            placeholder="Search widgets…"
                            value={searchQuery}
                            onChange={(e) =>
                              handleGroupSearch(chartType, e.target.value)
                            }
                            style={{
                              borderRadius: searchQuery ? "0" : "0 6px 6px 0",
                              outline: "none",
                              borderColor: "var(--bs-border-color)",
                              boxShadow: "none",
                            }}
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary border-start-0"
                              onClick={() => handleGroupSearch(chartType, "")}
                              aria-label="Clear search"
                              style={{ borderRadius: "0 6px 6px 0" }}
                            >
                              <i className="bi bi-x" />
                            </button>
                          )}
                        </div>
                      </div>

                      {filteredWidgets.length > 0 ? (
                        filteredWidgets.map((widget) => (
                          <button
                            key={widget.id}
                            type="button"
                            draggable
                            className={`builder-widget-card text-start border-0 w-100 ${
                              highlightedWidgetId === widget.id
                                ? "is-recently-clicked"
                                : ""
                            }${
                              draggingWidgetId === widget.id
                                ? " is-dragging"
                                : ""
                            }`}
                            onClick={() => handleWidgetClick(widget)}
                            onDragStart={(e) => handleDragStart(e, widget)}
                            onDragEnd={handleDragEnd}
                          >
                            <div
                              className="builder-widget-card__icon"
                              aria-hidden="true"
                            >
                              <i className={`bi ${widget.icon}`} />
                            </div>

                            <div className="builder-widget-card__content">
                              <div className="builder-widget-card__topline">
                                <span className="builder-widget-card__category">
                                  {widget.group}
                                </span>
                                <span className="ms-auto">
                                  {widget.defaultGridW} x {widget.defaultGridH}
                                </span>
                              </div>
                              <h3 className="builder-widget-card__title mb-0">
                                {widget.title}
                              </h3>
                              <p className="builder-widget-card__description mb-0">
                                {widget.description}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : (
                        <p className="text-muted small px-1 mb-0">
                          No widgets match &ldquo;{searchQuery}&rdquo;
                        </p>
                      )}
                    </div>
                  )}
                </section>
              );
            })}

            {dashboardTemplates.length === 0 && (
              <div className="text-muted small">
                No widget templates are available for the selected dashboard.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="builder-sidebar__footer">
        <div className="builder-sidebar__tip">
          <i className="bi bi-kanban" />
          <span>Click or drag any widget to add it to the layout.</span>
        </div>
        <div className="builder-sidebar__tip">
          <i className="bi bi-sliders" />
          <span>
            Widget default size is derived from chart configuration in the
            builder.
          </span>
        </div>
      </div>
    </aside>
  );
}
